import { useQuery } from "@apollo/client";
import { AppContext } from "AppContainer";
import Errors from "components/Errors";
import Page from "components/Page";
import SearchBar from "components/SearchBar";
import Spinner from "components/Spinner";
import { useContext } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchByProp } from "utils/search";
import {
  FETCH_SUPPLIER_STOCK_OVERVIEW,
  FETCH_SUPPLIER_STOCK_PRODUCTS,
} from "./graphql";

const InventoryList = () => {
  const { user } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams({ q: "" });
  const { loading, error, data } = useQuery(FETCH_SUPPLIER_STOCK_PRODUCTS, {
    variables: { supplierId: user.supplier.id },
    fechPolicy: "network-only",
  });
  const fetchOverview = useQuery(FETCH_SUPPLIER_STOCK_OVERVIEW, {
    variables: { supplierId: user.supplier.id },
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const overview = fetchOverview.data
    ? JSON.parse(fetchOverview.data.supplierStockOverview)
    : null;

  const filteredProducts = data.supplierStockProducts.filter((product) => {
    const searchText = searchParams.get("q") || "";
    return searchByProp(product, ["number", "name", "nameCn"], searchText);
  });

  const products = filteredProducts.map((i) => ({
    ...i,
    fc: overview ? overview[i.odooId]?.fc_qty : 0,
  }));

  const productsHasNoStock = overview ? products.filter((i) => i.fc <= 0) : [];

  return (
    <Page
      title="Inventory 库存"
      topbarRightView={
        <Link to="submit-delivery">Submit Inventory 提交库存</Link>
      }
    >
      <div className="mx-10 my-6 text-sm opacity-70">
        This page shows the inventory number Waboba owned in our supplier.
        <br />
        Waboba will manage the inventory numbers during our operation. You can
        also submit an inventory update anytime if you want then we will review
        and update the inventory number accordingly.
        <br />
        本页面显示Waboba在我们供应商处拥有的库存数量。Waboba将在我们的运营期间管理库存数量。您也可以随时提交库存更新，我们将审核并相应更新库存数量。
        {fetchOverview.error ? (
          <div className="mt-4 text-red-600 font-semibold">
            暂时无法读取所有产品的库存预计剩余数量，请稍后再试，我们可能正在调整库存数据。如果这个问题持续存在，请联系我们的相关负责人。
          </div>
        ) : null}
      </div>

      <div className="mx-8">
        <SearchBar
          placeholder="Search by product number, name, or Chinese name. 通过产品编号、名称搜索."
          value={searchParams.get("q") || ""}
          onChange={(text) => {
            setSearchParams((prev) => {
              const p = new URLSearchParams(prev);
              if (text.trim() === "") {
                p.delete("q");
              } else {
                p.set("q", text);
              }
              return p;
            });
          }}
        />
      </div>

      <div className="card m-8 mt-4 flex-1 flex flex-col overflow-auto p-0 rounded-xl">
        <div className="overflow-auto relative text-sm">
          <table>
            <thead>
              <tr className="sticky top-0 z-10 bg-gray-200">
                <th className="px-6 py-3">Product / 产品</th>
                <th className="px-6 py-3 text-right">
                  <div>Planning QTY / 计划使用</div>
                  <div className="text-xs opacity-70">
                    没有做PO,正在计划的数量
                  </div>
                </th>
                <th className="px-6 py-3 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <div>
                      <Spinner
                        size={16}
                        className={
                          fetchOverview.loading ? "opacity-100" : "opacity-0"
                        }
                      />
                    </div>
                    <div>
                      <div>Forecast QTY / 预计剩余数量</div>
                      <div className="text-xs opacity-70">
                        库存数量扣除所有PO以及计划剩余的数量
                      </div>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr
                  key={product.id}
                  className={`
                    hover:bg-blue-50 dark:hover:bg-gray-800 border-b dark:border-gray-700
                    ${
                      product.fc && product.fc <= 0
                        ? "text-red-600 bg-red-50 dark:text-red-500 dark:bg-red-900 dark:hover:bg-red-900 dark:bg-opacity-20 dark:hover:bg-opacity-50 border-red-100"
                        : "border-gray-100 dark:border-gray-100"
                    }
                  `}
                >
                  <td className="px-6">
                    <Link to={`${product.id}`}>
                      [{product.number}] {product.nameCn} - {product.name}
                    </Link>
                  </td>
                  <td className="px-6 text-right text-gray-800 dark:text-gray-100">
                    {product.supplierStockPlanningQty}
                  </td>
                  <td className="px-6 text-right text-gray-800 dark:text-gray-100">
                    {fetchOverview.loading ? "..." : product.fc}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="italic opacity-70 text-sm px-6 py-3 border-t border-gray-100 dark:border-gray-700">
          <span>Total {products.length} products. </span>
          {productsHasNoStock.length > 0 ? (
            <span className="text-red-600">
              {productsHasNoStock.length} products is running out of stock.
            </span>
          ) : null}
        </div>
      </div>
    </Page>
  );
};

export default InventoryList;
